export interface CurrencyOption {
  code: string;
  symbol: string;
  rate: number;
  locales: string[];
  enabled: boolean;
  is_default?: boolean;
}

export interface FormatOptions {
  /** Decimal places when |value| >= 1 (default: 0 for high-rate currencies like JPY, 2 otherwise) */
  digitsLarge?: number;
  /** Decimal places when |value| < 1 (default: same as digitsLarge) */
  digitsSmall?: number;
  /** Abbreviate large numbers (>= 1000 → "1k", >= 1M → "1M") */
  abbreviate?: boolean;
}

/**
 * Pure formatter that takes everything as arguments — no store imports.
 * Easier to unit-test, and lets callers compose with their own data sources.
 */
export function formatBalanceWith(
  quota: number,
  locale: string,
  currencies: CurrencyOption[],
  quotaPerUnit: number,
  options: FormatOptions = {},
): string {
  const usable = currencies.filter((c) => c.enabled);
  if (usable.length === 0 || !Number.isFinite(quotaPerUnit) || quotaPerUnit <= 0) {
    return `${quota}`;
  }

  const cfg =
    usable.find((c) => c.locales.includes(locale)) ??
    usable.find((c) => c.is_default) ??
    usable[0];

  const value = (quota / quotaPerUnit) * cfg.rate;

  // Default precision: 0 for high-rate currencies (e.g. JPY ¥80), 2 otherwise
  const defaultDigits = cfg.rate >= 10 ? 0 : 2;
  const absVal = Math.abs(value);
  const digits =
    absVal >= 1
      ? (options.digitsLarge ?? defaultDigits)
      : (options.digitsSmall ?? options.digitsLarge ?? defaultDigits);

  // Abbreviation: >= 1000 → "k", >= 1M → "M"
  if (options.abbreviate && absVal >= 1000) {
    let displayValue: number;
    let unit: string;
    if (absVal >= 1_000_000_000) {
      displayValue = value / 1_000_000_000;
      unit = 'B';
    } else if (absVal >= 1_000_000) {
      displayValue = value / 1_000_000;
      unit = 'M';
    } else {
      displayValue = value / 1_000;
      unit = 'k';
    }
    const abbrevDigits = Math.min(digits, 1);
    const abbrevStr = displayValue.toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: abbrevDigits,
    });
    return `${cfg.symbol}${abbrevStr}${unit}`;
  }

  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${cfg.symbol}${formatted}`;
}
